import { ArrowLeft } from "lucide-react";
import { Wordmark } from "./Nav";

const S = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-semibold tracking-tight text-diem-ink">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-diem-slate">{children}</div>
  </section>
);

const L = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: 28 August 2026",
    body: (
      <>
        <S title="1. Who we are">
          <p>Deyem ("we", "us") is a pre-launch messaging platform. This policy explains what information this website (deyem.co.uk) collects and how we use it. For anything privacy-related, contact us at privacy@deyem.co.uk.</p>
        </S>
        <S title="2. What we collect">
          <L items={[
            "Waitlist sign-ups: your email address (and name, if you provide it) so we can tell you when Deyem launches.",
            "Contact form messages: your name, email address and message, so we can reply.",
            "Payment information: if you become a Founding Member, payment is processed by Stripe. We never see or store your card details — Stripe handles those under its own privacy policy.",
            "Usage data: we may use privacy-respecting product analytics to understand how the website is used (pages visited, approximate device and region).",
          ]} />
        </S>
        <S title="3. How we use it">
          <L items={[
            "To operate the waitlist and send you launch updates (no spam — you can unsubscribe at any time).",
            "To respond to enquiries sent through the contact form.",
            "To process Founding Member subscriptions and keep records of transactions.",
            "To improve the website and understand what visitors find useful.",
          ]} />
          <p>We do not sell your personal information, and we do not share it with third parties except the processors needed to run this website (such as Stripe for payments and our hosting provider).</p>
        </S>
        <S title="4. How long we keep it">
          <p>Waitlist and contact data is kept until Deyem launches and a short period after, or until you ask us to delete it. Payment records are kept as required for accounting and tax purposes.</p>
        </S>
        <S title="5. Your rights">
          <p>Under UK data protection law you have the right to:</p>
          <L items={[
            "Access the personal data we hold about you.",
            "Ask us to correct or delete it.",
            "Object to or restrict how we use it.",
            "Ask for a copy in a portable format.",
            "Complain to the Information Commissioner's Office (ICO) if you're unhappy with how we handle your data.",
          ]} />
          <p>To exercise any of these, email privacy@deyem.co.uk.</p>
        </S>
        <S title="6. Security">
          <p>We use secure hosting, encrypted connections (HTTPS) and limit access to the data we hold. No method of transmission over the internet is perfectly secure, but we work to protect your information and will tell you if something goes wrong that affects you.</p>
        </S>
        <S title="7. Changes">
          <p>We may update this policy as Deyem develops. The date at the top shows the latest version; significant changes will be flagged on this page.</p>
        </S>
      </>
    ),
  },
  terms: {
    title: "Terms of Service",
    updated: "Last updated: 28 August 2026",
    body: (
      <>
        <S title="1. The service">
          <p>These terms govern your use of the Deyem website (deyem.co.uk) and the pre-launch services offered through it, including the waitlist and Founding Member subscriptions. Deyem itself — the messaging application — is currently in development and is not yet publicly available. By using this website you agree to these terms.</p>
        </S>
        <S title="2. Waitlist">
          <p>Joining the waitlist is free and non-binding. It registers your interest and means we'll email you when Deyem is ready. It does not guarantee access, pricing or features.</p>
        </S>
        <S title="3. Founding Member subscriptions">
          <L items={[
            "A Founding Member subscription reserves a Deyem Project Owner plan at £10/month or £100/year, starting with a 30-day free trial.",
            "Payments are processed by Stripe. Your subscription begins at checkout; the first payment is taken when the trial ends unless you cancel before then.",
            "You can cancel at any time. If you cancel during the trial, you are charged nothing.",
            "Because Deyem is pre-launch, the Project Owner plan activates when the app becomes available to you. If we fail to launch, Founding Member payments taken will be refunded.",
            "Prices may change for new subscribers in future; your Founding Member price is honoured while your subscription stays active.",
          ]} />
        </S>
        <S title="4. Acceptable use">
          <p>You agree not to misuse this website: no attempting to breach security, scrape at abusive volumes, impersonate others, or use the site for anything unlawful.</p>
        </S>
        <S title="5. Intellectual property">
          <p>The Deyem name, logo, design and content on this website belong to us. You're welcome to link to the site; you're not permitted to copy its design or content for your own product.</p>
        </S>
        <S title="6. Disclaimers and liability">
          <p>This website is provided "as is". We work hard to keep it accurate and available, but we don't warrant uninterrupted operation. Nothing in these terms excludes liability that cannot be excluded under law, including for death or personal injury caused by negligence. Otherwise, to the extent permitted by law, our liability for anything arising from your use of this website is limited to the amount you have paid us in the previous 12 months (or £100 if you have paid nothing).</p>
        </S>
        <S title="7. Changes">
          <p>We may update these terms as the service develops; the date at the top shows the latest version. Continuing to use the website after changes means you accept them.</p>
        </S>
        <S title="8. Governing law">
          <p>These terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction.</p>
          <p>Questions: legal@deyem.co.uk.</p>
        </S>
      </>
    ),
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: 28 August 2026",
    body: (
      <>
        <S title="1. What cookies we use">
          <p>This website aims to use as few cookies as possible.</p>
          <L items={[
            "Strictly necessary: cookies or similar storage required for the website and Stripe checkout to function and stay secure.",
            "Analytics: we may use product analytics to understand aggregate website usage (which pages are visited, for how long). These help us improve the site and are not used to build advertising profiles.",
          ]} />
        </S>
        <S title="2. What we don't do">
          <p>We do not use advertising cookies, and we do not sell or share cookie data with advertisers.</p>
        </S>
        <S title="3. Your choices">
          <p>You can control or delete cookies through your browser settings at any time. Blocking analytics cookies will not affect how the website works for you.</p>
          <p>Questions: privacy@deyem.co.uk.</p>
        </S>
      </>
    ),
  },
};

export const LegalPage = ({ doc }) => {
  const d = DOCS[doc];
  return (
    <div className="min-h-screen bg-diem-paper" data-testid={`legal-${doc}-page`}>
      <header className="px-6 lg:px-8 h-[72px] flex items-center max-w-4xl mx-auto w-full">
        <a href="/" aria-label="Deyem home" data-testid="legal-home-link"><Wordmark /></a>
      </header>
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-diem-blue">{d.updated}</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tighter text-diem-ink" data-testid="legal-title">
          {d.title}
        </h1>
        <div className="mt-6 rounded-3xl bg-white border border-diem-line p-7 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {d.body}
        </div>
        <p className="mt-6 text-[12px] text-diem-muted leading-relaxed">
          This page should be reviewed by a qualified legal professional before Deyem's public launch.
        </p>
        <a href="/" data-testid="legal-back-link"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-6 py-3 text-sm shadow-[0_10px_28px_rgba(0,85,255,0.4)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
          <ArrowLeft className="w-4 h-4" /> Back to Deyem
        </a>
      </main>
    </div>
  );
};
